import {useContext, useEffect, useState} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {Menu, MenuItem, Sidebar, SubMenu} from 'react-pro-sidebar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AuthContext from '../../contexts/auth-context';
import AppContext from '../../contexts/app-context';
import {getNavigationConfig} from '../config/navigationConfig';
import {app_paths, appLogoPath} from '../config/variables';
import PatientListSection from './PatientListSection';

// Icon logo path for collapsed sidebar
const iconLogoPath = "/images/logo/veritas/black/icon-black.png";

const NavigationSidebar = ({collapsed, onToggleCollapse}) => {
    const authCtx = useContext(AuthContext);
    const appCtx = useContext(AppContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [openSubMenus, setOpenSubMenus] = useState({});

    const userRole = appCtx.userRole;
    const userName = authCtx.userProfile?.user?.name || '';

    const navigationItems = getNavigationConfig(userRole, userName);

    // Close all submenus when sidebar collapses
    useEffect(() => {
        if (collapsed) {
            setOpenSubMenus({});
        }
    }, [collapsed]);

    const handleLogout = () => {
        authCtx.logout();
        navigate(app_paths.LOGIN_AUTH);
    };

    const isActive = (path) => {
        if (!path) return false;
        // Exact match for most routes
        if (location.pathname === path) return true;
        // Handle dynamic routes - check if current path starts with the base path
        // For example: /doctor/JohnDoe should match when path is /doctor/...
        if (path.includes('doctor') && location.pathname.includes('/doctor/')) return true;
        // Exact match for /patients page
        if (path === '/patients' && location.pathname === '/patients') return true;
        // Handle /patient/:id routes (but not /patients)
        if (path.includes('/patient/') && location.pathname.includes('/patient/') && !location.pathname.includes('/patients')) return true;
        return false;
    };

    const handleSubMenuToggle = (itemId) => {
        setOpenSubMenus((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    };

    const renderMenuItem = (item) => {
        // Handle logout item
        if (item.isLogout) {
            return (
                <MenuItem
                    key={item.id}
                    icon={<item.icon style={{fontSize: '24px'}}/>}
                    onClick={handleLogout}
                    style={{
                        color: '#d32f2f',
                    }}
                >
                    {item.label}
                </MenuItem>
            );
        }

        // Handle items with children (SubMenu)
        if (item.children) {
            // If parent has a path, make it clickable
            const handleParentClick = item.path ? () => navigate(item.path) : undefined;

            return (
                <SubMenu
                    key={item.id}
                    label={item.label}
                    icon={<item.icon style={{fontSize: '24px'}}/>}
                    open={!collapsed && openSubMenus[item.id]}
                    onOpenChange={() => !collapsed && handleSubMenuToggle(item.id)}
                    onClick={handleParentClick}
                    active={item.path && isActive(item.path)}
                >
                    {!collapsed && item.children.map((child) => {
                        // Special handling for patient list
                        if (child.showPatientList) {
                            return (
                                <Box key={child.id} sx={{py: 1}}>
                                    <PatientListSection/>
                                </Box>
                            );
                        }

                        // Regular child menu item
                        return (
                            <MenuItem
                                key={child.id}
                                icon={child.icon && <child.icon/>}
                                component={<NavLink to={child.path}/>}
                                active={isActive(child.path)}
                            >
                                {child.label}
                            </MenuItem>
                        );
                    })}
                </SubMenu>
            );
        }

        // Handle regular menu items
        return (
            <MenuItem
                key={item.id}
                icon={<item.icon style={{fontSize: '24px'}}/>}
                component={<NavLink to={item.path}/>}
                active={isActive(item.path)}
            >
                {item.label}
            </MenuItem>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100%',
                minHeight: '100vh',
                position: 'relative',
                '& .ps-sidebar-root': {
                    border: 'none',
                    height: '100%',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                },
                '& .ps-sidebar-container': {
                    backgroundColor: '#f8f9fa',
                    height: '100%',
                },
                '& .ps-menu-button': {
                    padding: '14px 16px',
                    marginBottom: '12px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                },
                '& .ps-menu-button.ps-active': {
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    fontWeight: 600,
                    '& .ps-menu-icon': {
                        color: 'primary.main',
                    },
                },
                '& .ps-submenu-content': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .ps-menuitem-root': {
                    marginBottom: '4px',
                },
            }}
        >
            <Sidebar
                collapsed={collapsed}
                width="340px"
                collapsedWidth="80px"
                transitionDuration={150}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'space-between',
                        padding: '16px',
                        height: '88px', // Fixed height to prevent menu items from shifting
                    }}
                >
                    {collapsed ? (
                        <NavLink to="/" style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                            <img
                                src={iconLogoPath}
                                alt="logo"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </NavLink>
                    ) : (
                        <NavLink to="/" style={{display: 'flex', alignItems: 'center', marginLeft: '18px'}}>
                            <img
                                src={appLogoPath}
                                alt="logo"
                                style={{
                                    maxWidth: '160px',
                                    maxHeight: '42px',
                                    objectFit: 'contain',
                                }}
                            />
                        </NavLink>
                    )}
                </Box>

                {/* Navigation Menu */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pt: 3,
                        pb: 2,
                        px: 1,
                    }}
                >
                    <Menu>{navigationItems.map((item) => renderMenuItem(item))}</Menu>
                </Box>
            </Sidebar>

            {/* Toggle Arrow Button at Edge */}
            <IconButton
                onClick={onToggleCollapse}
                sx={{
                    position: 'absolute',
                    right: {sm: '5px', md: '-16px'},
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: '32px',
                    height: '32px',
                    zIndex: 1100,
                    '&:hover': {
                        backgroundColor: '#e9ecef',
                    },
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
            >
                {collapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
            </IconButton>
        </Box>
    );
};

export default NavigationSidebar;
